import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function CountIndicator({ count, style }) {
  if (count == null) return null;

  if (style === 'trash') {
    return <span className="ms-2 text-secondary fw-light">{count}</span>;
  }

  if (count <= 0) return null;

  if (style === 'stroke') {
    return (
      <span className="badge rounded-pill border border-accent-blue-50 text-accent-blue-50 fw-normal ms-2">{count}</span>
    );
  }

  return <span className="badge rounded-pill bg-accent-blue-50 ms-2">{count}</span>;
}

CountIndicator.propTypes = {
  count: PropTypes.number,
  style: PropTypes.oneOf(['filled', 'stroke', 'trash']),
};

function Tab({
  label, count, countStyle, isActive, onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classnames(
        'bg-transparent border-top-0 border-start-0 border-end-0 border-bottom flex-fill pt-4 pb-2 px-3 fw-medium paragraph-sm d-flex align-items-center justify-content-center flex-row',
        isActive ? 'border-2 text-primary-60 border-primary-60' : 'text-secondary',
      )}
    >
      {label}
      <CountIndicator count={count} style={countStyle} />
    </button>
  );
}

Tab.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number,
  countStyle: PropTypes.oneOf(['filled', 'stroke', 'trash']),
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * MailboxTabs - Focused/Everything/Trash tab navigation
 *
 * Media users see all three tabs:
 * - Focused: filled badge with unread count (primary attention magnet).
 * - Everything: stroke badge with unread count (de-emphasized).
 * - Trash: plain text total count, always visible.
 *
 * Promoters don't have a Focused tab — they see "Inbox" (= everything mailbox,
 * no filter) with a filled unread badge + Trash with the plain text count.
 */
function MailboxTabs({
  activeMailbox = 'focused',
  onSelect,
  counts = { focused: 0, everything: 0, trash: 0 },
  isMedia = true,
}) {
  const handleClick = (mailbox, e) => {
    e?.preventDefault();
    onSelect(mailbox);
  };

  return (
    <div className="d-flex">
      {isMedia && (
        <Tab
          label="Focused"
          count={counts.focused}
          countStyle="filled"
          isActive={activeMailbox === 'focused'}
          onClick={(e) => handleClick('focused', e)}
        />
      )}
      <Tab
        label={isMedia ? 'Everything' : 'Inbox'}
        count={counts.everything}
        // Media: de-emphasized stroke so Focused remains primary. Promoter: filled
        // since Inbox is their only non-Trash tab and owns the unread badge.
        countStyle={isMedia ? 'stroke' : 'filled'}
        isActive={activeMailbox === 'everything'}
        onClick={(e) => handleClick('everything', e)}
      />
      <Tab
        label="Trash"
        count={counts.trash}
        countStyle="trash"
        isActive={activeMailbox === 'trash'}
        onClick={(e) => handleClick('trash', e)}
      />
    </div>
  );
}

MailboxTabs.propTypes = {
  activeMailbox: PropTypes.oneOf(['focused', 'everything', 'trash']),
  onSelect: PropTypes.func.isRequired,
  counts: PropTypes.shape({
    focused: PropTypes.number,
    everything: PropTypes.number,
    trash: PropTypes.number,
  }),
  isMedia: PropTypes.bool,
};

export default MailboxTabs;
